import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Text,
} from '@chakra-ui/react';
import { useFindUserCapturedPropertyDetailsByAddressLazyQuery } from '@client/graphql/__generated__/main-operations';
import {
  PropertyDetailsFragment,
  UsState,
} from '@client/graphql/__generated__/types';
import { GeocodingFeature } from '@mapbox/search-js-core';
import { useFormikContext } from 'formik';
import { useState } from 'react';
import { UsStateSelect } from '~/services/document/common/UsStateSelect';
import { TextField } from '../../../Formik/TextField';
import { MapboxGeocoder } from '../../../MapboxGeocoder/MapboxGeocoder';
import { getAddressInputFromSearchResult } from '../../../MapSearch/helpers';
import { HorizontalPropertyDetailsCard } from '../HorizontalPropertyDetailsCard';
import { OffMarketListingFormData } from './constants';

interface AddressStepProps {
  setSelectedGeocode: (geocode: GeocodingFeature) => void;
  setShowAddressFields: (show: boolean) => void;
  showAddressFields: boolean;
}

export const AddressStep = ({
  setSelectedGeocode,
  setShowAddressFields,
  showAddressFields,
}: AddressStepProps) => {
  const formik = useFormikContext<OffMarketListingFormData>();
  const [existingPropertyDetails, setExistingPropertyDetails] = useState<
    PropertyDetailsFragment | undefined
  >(undefined);
  const [findUserCapturedPropertyDetailsByAddress] =
    useFindUserCapturedPropertyDetailsByAddressLazyQuery();

  const onRetrieve = (value: GeocodingFeature) => {
    const { streetAddress, city, state, zipCode, unit } =
      getAddressInputFromSearchResult(value);

    void (async () => {
      const { data } = await findUserCapturedPropertyDetailsByAddress({
        variables: {
          input: {
            addressInput: {
              streetAddress,
              city,
              state: state as UsState,
              zipCode,
              unit,
            },
          },
        },
      });

      if (data?.findUserCapturedPropertyDetailsByAddress) {
        setExistingPropertyDetails(
          data.findUserCapturedPropertyDetailsByAddress
        );
      } else {
        await formik.setFieldValue(
          'streetAddress',
          value.properties.context.address?.name || ''
        );

        await formik.setFieldValue(
          'city',
          value.properties.context.place?.name || ''
        );

        await formik.setFieldValue(
          'state',
          (
            value.properties.context.region as
              | { region_code: string }
              | undefined
          )?.region_code || ''
        );

        await formik.setFieldValue(
          'zipCode',
          value.properties.context.postcode?.name || ''
        );

        await formik.setFieldValue(
          'unit',
          (
            value.properties.context as {
              secondary_address: { name: string }; // Mapbox GeocodingFeatureContext type is not complete
            }
          )?.secondary_address?.name || ''
        );

        setShowAddressFields(true);
      }

      setSelectedGeocode(value);
    })();
  };

  return (
    <>
      <FormControl>
        <FormLabel>Search Address</FormLabel>
        <MapboxGeocoder
          allowedFeatureTypes={new Set(['address'])}
          bg="input.bg"
          placeholder="Search Address"
          onRetrieve={onRetrieve}
        />
      </FormControl>

      {existingPropertyDetails && (
        <Flex direction="column" gap={2}>
          <Text>Matching existing off market listing:</Text>
          <HorizontalPropertyDetailsCard
            propertyDetails={existingPropertyDetails}
          />
        </Flex>
      )}

      {!showAddressFields && !existingPropertyDetails && (
        <Button
          alignSelf="flex-start"
          size="sm"
          variant="link"
          onClick={() => setShowAddressFields(true)}
        >
          Manually input fields
        </Button>
      )}

      {showAddressFields && (
        <>
          <FormControl isRequired>
            <FormLabel>Address</FormLabel>
            <TextField name="streetAddress" />
          </FormControl>
          <HStack align="flex-start" spacing={3}>
            <FormControl>
              <FormLabel>Unit</FormLabel>
              <TextField name="unit" />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>City</FormLabel>
              <TextField name="city" />
            </FormControl>
          </HStack>
          <HStack align="flex-start" spacing={3}>
            <FormControl isRequired>
              <FormLabel>State</FormLabel>
              <UsStateSelect
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
                value={
                  formik.values.state
                    ? { label: formik.values.state, value: formik.values.state }
                    : null
                }
                onChange={(value) => {
                  void formik.setFieldValue('state', value?.value);
                }}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Zip code</FormLabel>
              <TextField name="zipCode" />
            </FormControl>
          </HStack>
        </>
      )}
    </>
  );
};
